import type {
	ClientConditionApiModel,
	ClientPhysicalLocationApiModel,
	SearchLimitationResult,
} from '@api_patient/generated/clientv2/models';
import { UnitsApiService } from '@api_patient/units_api.service';
import { AnalyticCategories, AnalyticService } from '@application/analytic/analytic.service';
import { UnitService } from '@application/unit.service';
import { UniversalConfig } from '@application/universal_config.service';
import { AsyncSignalValue } from '@component/async.component';
import { Logger } from '@visiba/logger';
import { RouterService } from '@cortex-shim/router.service';
import { signal } from '@preact/signals-react';
import { ControllerProtocol, Injectable, Scopes } from '@visiba/instantiation';
import { BookingBasketService } from './booking_basket.service';
import { NiN } from '@visiba/national-identity-number';
import { AuthService } from '@application/authentication/auth.service';
import { type BookingSettings, ViewBookingSettingsService } from './view_booking_settings.service';
import { RedRobinApiService } from '@api_patient/red_robin_api.service';
import { safeNumberParse } from '@visiba/std';
import { SearchLimitationApiService } from '@api_patient/search_limitation_api.service';
import { DateService } from '@application/time/date.service';
import dayjs from 'dayjs';

export enum BookingScreen {
	WhoIsRespondant = 'who-is-respondant',
	ReasonForVisit = 'reason-for-visit',
	Location = 'location',
	TimeSlot = 'time-slot',
	Forms = 'forms',
	Conclusion = 'conclusion',
	Done = 'done',
	Error = 'error',
}

interface FlowData {
	currentCountry: keyof NiN.Implementations;
	allowRepresentative: boolean;
	useReasonForVisits: boolean;
	reasonForVisits: ClientConditionApiModel[];
	physicalLocations: ClientPhysicalLocationApiModel[];
}

interface FlowSettings extends BookingSettings {
	searchContext: Nullable<SearchLimitationResult>;
}

@Injectable({
	scope: Scopes.Scoped,
})
export class ViewBookingController implements ControllerProtocol {
	public flowData = signal<AsyncSignalValue<FlowData>>({
		value: null,
		flowState: 'pending',
	});
	public flowSettings = signal<AsyncSignalValue<FlowSettings>>({
		value: null,
		flowState: 'pending',
	});

	public basketReady = signal(false);
	private readonly logger = new Logger('ViewBookingController');
	private readonly routeBase: string;

	constructor(
		private readonly routerService: RouterService,
		private readonly universalConfig: UniversalConfig,
		private readonly unitService: UnitService,
		private readonly unitsApiService: UnitsApiService,
		private readonly analyticService: AnalyticService,
		private readonly bookingBasketService: BookingBasketService,
		private readonly authService: AuthService,
		private readonly bookingSettingsService: ViewBookingSettingsService,
		private readonly redRobinApiService: RedRobinApiService,
		private readonly searchLimitationApiService: SearchLimitationApiService,
		private readonly dateService: DateService,
	) {
		const unit = this.unitService.getUnitNodeInTree(this.universalConfig.selectedUnitId);
		this.routeBase = unit.routeWithoutLicenceholder;
	}

	async whenMount() {
		this.analyticService.trackPageView({
			customTitle: 'Booking',
		});

		this.logger.logInformation('Mounting ViewBookingController');

		this.flowData.value = {
			value: null,
			flowState: 'pending',
		};

		const unitConfigResult = await this.unitsApiService.getUnitConfig(this.universalConfig.selectedUnitId);
		if (unitConfigResult.err) return Err(unitConfigResult.err);

		const allowRepresentative = unitConfigResult.ok.data.Offer?.AllowClientAgents || false;
		const useReasonForVisits = unitConfigResult.ok.data.Offer?.HasConditionWorkflow || false;
		let reasonForVisits: ClientConditionApiModel[] = [];

		if (useReasonForVisits) {
			const reasonForVisitsResult = await this.fetchReasonForVisits();

			if (reasonForVisitsResult.err) {
				this.flowData.value = {
					value: null,
					flowState: 'error',
				};

				return;
			}

			reasonForVisits = reasonForVisitsResult.ok;
		}

		const physicalLocationsResult = await this.fetchPhysicalLocations();

		if (physicalLocationsResult.err) {
			this.flowData.value = {
				value: null,
				flowState: 'error',
			};

			return;
		}

		this.bookingBasketService.load({
			useReasonForVisits,
		});

		const userNationality = this.authService.getUser()?.nationality;

		this.flowData.value = {
			value: {
				currentCountry:
					userNationality && NiN.supportsCountryCode(userNationality)
						? (userNationality as keyof NiN.Implementations)
						: this.universalConfig.selectedCountryCode.peek(),
				allowRepresentative,
				useReasonForVisits: useReasonForVisits,
				reasonForVisits: reasonForVisits,
				physicalLocations: physicalLocationsResult.ok,
			},
			flowState: 'accessible',
		};
	}

	public async restoreBasket() {
		const restoreResult = await this.bookingBasketService.restore();
		if (restoreResult.err) {
			this.bookingBasketService.clear();
			this.goToScreen(BookingScreen.WhoIsRespondant, { replace: true });

			return;
		}

		// We need to unlock the time slot and lock it again to make sure the time
		// slot is locked to current user. Otherwise if the user locked a time slot
		// when it wasn't logged in and the login after, the time slot is still locked
		// to the previous anonymous user.
		const { timeSlotLockId, timeSlotId } = this.bookingBasketService.get();
		if (timeSlotLockId && timeSlotId) {
			const unlockResult = await this.bookingBasketService.unlockTimeSlot();
			if (unlockResult.err) {
				// We don't care if unlock fails
			}

			const lockResult = await this.bookingBasketService.lockTimeSlot(timeSlotId);
			if (lockResult.err) {
				this.bookingBasketService.set({
					timeSlotLockId: null,
					timeSlotId: null,
				});
			}
		}

		this.basketReady.value = true;
	}

	public goToScreen(
		screen: BookingScreen,
		options: {
			replace?: boolean;
			locationType?: 'physical' | 'digital';
			locationId?: number;
			formId?: number;
			formPage?: number;
			ticket?: string;
		} = {},
	): void {
		let path = '';
		const analyticActionName = screen;

		switch (screen) {
			case BookingScreen.WhoIsRespondant:
				path = 'who-is-respondant';
				break;
			case BookingScreen.ReasonForVisit:
				path = 'reason-for-visit';
				break;
			case BookingScreen.Location:
				path = 'location';
				break;
			case BookingScreen.TimeSlot:
				path = 'time-slot';

				if (options.locationType === 'digital') {
					path += '/digital';
				} else if (options.locationType === 'physical' && options.locationId) {
					path += `/${options.locationId}`;
				}
				break;
			case BookingScreen.Forms:
				path = 'forms';

				if (options.formId) {
					path += `/${options.formId}`;

					if (options.formPage) {
						path += `/${options.formPage}`;
					}
				}
				break;
			case BookingScreen.Conclusion:
				path = 'conclusion';
				break;
			case BookingScreen.Done:
				path = 'done';

				if (options.ticket) {
					path += `/${options.ticket}`;
				}
				break;
			case BookingScreen.Error:
				path = 'error';
				break;
		}

		this.analyticService.trackEvent(AnalyticCategories.Booking, ViewBookingAnalyticActions.GoToScreen, analyticActionName);

		const url = [this.routeBase, 'booking', path].filter(Boolean);

		this.routerService.navigateByUrl(`/${url.join('/')}`, { replace: options.replace });
	}

	public goToReceptionHome() {
		this.routerService.navigateByUrl(`/${this.routeBase}`);
	}

	public async setupNewBasket() {
		this.clearBasket();

		const queryParams = new URLSearchParams(window.location.search);

		const redRobinSessionId = queryParams.get('redRobinSessionId');
		let representativeNin = null;

		if (redRobinSessionId) {
			const redRobinResult = await this.redRobinApiService.getRepresentativeNin(redRobinSessionId);

			if (redRobinResult.err) {
				this.logger.logError('Failed to get RedRobin session');

				return Err(redRobinResult.err);
			}

			representativeNin = redRobinResult.ok.data.RepresentativeNin;
		}

		const deepLinkParams = {
			dom: queryParams.get('dom') ?? null,
			rfv:
				queryParams
					.get('rfv')
					?.split(',')
					.map((id) => safeNumberParse(id))
					.filter((id) => id != null) ?? null,
			attachFiles: queryParams.get('file') ? queryParams.get('file') !== 'false' : null,
			lockFilters: queryParams.get('lock') ? queryParams.get('lock') === 'true' : null,
			resourceId: queryParams.get('r') ? safeNumberParse(queryParams.get('r') ?? '') : null,
			specialityId: queryParams.get('s') ? safeNumberParse(queryParams.get('s') ?? '') : null,
			unitId: queryParams.get('u') ? safeNumberParse(queryParams.get('u') ?? '') : null,
			locationId: queryParams.get('l') ? safeNumberParse(queryParams.get('l') ?? '') : null,
			locationType: queryParams.get('lt') ?? null,
			fromDate: queryParams.get('f') ?? null,
			toDate: queryParams.get('t') ?? null,
			searchContextId: queryParams.get('searchContextId') ?? null,
		};

		const flowSettings: BookingSettings = {
			redRobinSessionId: redRobinSessionId,
			representativeNin,
			descriptionOfMatter: deepLinkParams.dom,
			reasonForVisit: deepLinkParams.rfv,
			attachFiles: deepLinkParams.attachFiles ?? true,
			lockFilters: deepLinkParams.lockFilters ?? false,
			filterResourceId: deepLinkParams.resourceId,
			filterSpecialityId: deepLinkParams.specialityId,
			filterUnitId: deepLinkParams.unitId,
			locationId: deepLinkParams.locationId,
			locationType: deepLinkParams.locationType,
			searchContextId: deepLinkParams.searchContextId,
			fromDate: deepLinkParams.fromDate ? this.dateService.newDate(deepLinkParams.fromDate) : null,
			toDate: deepLinkParams.toDate ? this.dateService.newDate(deepLinkParams.toDate) : null,
		};

		this.bookingSettingsService.setSettings(flowSettings);

		this.goToScreen(BookingScreen.WhoIsRespondant, { replace: true });

		return Ok();
	}

	public clearBasket(): void {
		this.bookingBasketService.clear();
		this.bookingBasketService.unlockTimeSlot();
		this.basketReady.value = true;
	}

	public async abortBooking(): AsyncResult<void> {
		await this.bookingBasketService.unlockTimeSlot();
		this.bookingBasketService.clear();
		this.goToReceptionHome();

		return Ok();
	}

	public async loadSettings() {
		this.logger.logInformation('Load settings');

		let settings = this.bookingSettingsService.getSettings();
		let flowSettings: FlowSettings = {
			...settings,
			searchContext: null,
		};

		if (settings.searchContextId != null) {
			const limitationResult = await this.searchLimitationApiService.getSearchLimitation(
				this.universalConfig.selectedUnitId,
				settings.searchContextId,
			);
			if (limitationResult.err) {
				this.flowSettings.value = {
					value: null,
					flowState: 'error',
				};

				return;
			}

			const data = limitationResult.ok.data;
			const overrideSettings: Partial<BookingSettings> = {};

			if (data.HcpId) {
				overrideSettings.filterResourceId = data.HcpId;
			}

			if (data.PhysicalLocationId) {
				overrideSettings.locationId = data.PhysicalLocationId;
			}

			if (Object.keys(overrideSettings).length > 0) {
				overrideSettings.lockFilters = true;
			}

			flowSettings = {
				...settings,
				...overrideSettings,
				searchContext: data,
			};
		}

		this.flowSettings.value = {
			value: flowSettings,
			flowState: 'accessible',
		};
	}

	public getFlowSettings(): FlowSettings {
		if (this.flowSettings.value.flowState !== 'accessible') {
			throw new Error('Flow settings not accessible yet.');
		}

		return this.flowSettings.value.value;
	}

	public getFlowData(): FlowData {
		if (this.flowData.value?.flowState === 'accessible') {
			return this.flowData.value.value;
		}

		throw new Error("FlowData isn't accessible yet. Make sure to run whenMount() before trying to access flowData.");
	}

	public getFilteredReasonForVisits(): ClientConditionApiModel[] {
		let filteredReasonForVisits: ClientConditionApiModel[] = [];

		const flowSettings = this.getFlowSettings();
		const flowData = this.getFlowData();

		if (flowSettings.reasonForVisit) {
			if (flowSettings.reasonForVisit.length > 0) {
				filteredReasonForVisits = flowData.reasonForVisits.filter((rfv) => flowSettings.reasonForVisit?.includes(rfv.Id)) ?? [];
			}
		} else {
			filteredReasonForVisits = flowData.reasonForVisits ?? [];
		}

		return filteredReasonForVisits;
	}

	public getSelectedReasonForVisit(): Nullable<ClientConditionApiModel> {
		const selectedReasonForVisit = this.bookingBasketService.get().reasonForVisit;

		return this.getFilteredReasonForVisits().find((rfv) => rfv.Id === selectedReasonForVisit) ?? null;
	}

	private async fetchReasonForVisits(): AsyncResult<ClientConditionApiModel[]> {
		this.logger.logInformation('Get reason for visits');

		const unitConfigResult = await this.unitsApiService.getUnitConfig(this.universalConfig.selectedUnitId);
		if (unitConfigResult.err) return Err(unitConfigResult.err);

		if (unitConfigResult.ok.data.Offer?.HasConditionWorkflow === false) {
			return Ok([]);
		}

		const reasonForVisitsResult = await this.unitsApiService.getConditions(this.universalConfig.selectedUnitId);
		if (reasonForVisitsResult.err) {
			this.logger.logError(
				'User tried to get reason for visits: {message} {statusCode}',
				reasonForVisitsResult.err.message,
				reasonForVisitsResult.err.statusCode,
			);

			return Err(reasonForVisitsResult.err);
		}

		return Ok(reasonForVisitsResult.ok.data);
	}

	private async fetchPhysicalLocations(): AsyncResult<ClientPhysicalLocationApiModel[]> {
		this.logger.logInformation('Get physical locations');

		const unitConfigResult = await this.unitsApiService.getUnitConfig(this.universalConfig.selectedUnitId);
		if (unitConfigResult.err) return Err(unitConfigResult.err);

		if (unitConfigResult.ok.data.Offer?.HasPhysicalLocation === false) {
			return Ok([]);
		}

		const physicalLocationResult = await this.unitsApiService.getPhysicalLocations(
			this.universalConfig.selectedUnitId,
			this.universalConfig.selectedLanguageCode.peek(),
		);

		if (physicalLocationResult.err) {
			this.logger.logError(
				'User tried to get physical locations: {message} {statusCode}',
				physicalLocationResult.err.message,
				physicalLocationResult.err.statusCode,
			);

			return Err(physicalLocationResult.err);
		}

		return Ok(physicalLocationResult.ok.data);
	}
}

export enum ViewBookingAnalyticActions {
	GoToScreen = 'Go-to-screen',
	NotAllowedToBeHere = 'Not allowed to be here',
	DoesNotAllowRepresentative = 'Does not allow representative',
	AutoSetRespondantTypeRepresentative = 'Automatically respondant type set to "representative"',
	AutoSetRespondantTypeMe = 'Automatically respondant type set to "me"',
	AutoSetReasonForVisit = 'Automatically set reason for visit',
	SelectReasonForVisit = 'Select reason for visit',
	NoReasonForVisits = 'No reason for visits',
	AutoSetLocation = 'Automatically set location',
	OnlyDigital = 'Only digital',
	SelectLocation = 'Select location',
	ErrorFetchingAvailability = 'Error fetching availability',
	ErrorFetchingTimeSlots = 'Error fetching time slots',
	OpenTimeSlotDetails = 'Open time slot details',
	CloseTimeSlotDetails = 'Close time slot details',
	ErrorFetchingTimeSlotDetails = 'Error fetching time slot details',
	ErrorWhileLockingTimeSlot = 'Error while locking time slot',
	SelectTimeSlot = 'Select time slot',
	SelectTimeSlotAndLogin = 'Select time slot and login',
	NoDynamicForms = 'No dynamic forms',
	NumberOfDynamicForms = 'Number of dynamic forms',
	ErrorFetchingDynamicForms = 'Error fetching dynamic forms',
	ErrorFetchingTimeSlot = 'Error fetching time slot',
	FormIsInvalid = 'Form is invalid',
	SelectedTimeSlotNotIsAvailable = 'Selected time slot is not available',
	OpenedTimeSlotNotAvailableDialog = 'Opened time slot not available dialog',
	ClosedTimeSlotNotAvailableDialog = 'Closed time slot not available dialog',
}
